import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';
import { Database } from '../types/database.types';

type Plan = Database['public']['Tables']['plans']['Row'];
type Organization = Database['public']['Tables']['organizations']['Row'] & {
  plans: Plan;
};

type OrgContextType = {
  org: Organization | null;
  role: 'admin' | 'fin_ops' | 'employee' | null;
  loading: boolean;
  refreshOrg: (showLoading?: boolean) => Promise<void>;
};

const OrgContext = createContext<OrgContextType>({
  org: null,
  role: null,
  loading: true,
  refreshOrg: async () => {},
});

export const OrgProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [org, setOrg] = useState<Organization | null>(null);
  const [role, setRole] = useState<'admin' | 'fin_ops' | 'employee' | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadedUserId, setLoadedUserId] = useState<string | null>(null);

  const fetchOrg = useCallback(async (showLoading = true) => {
    if (!user) {
      setOrg(null);
      setRole(null);
      setLoadedUserId(null);
      setLoading(false);
      return;
    }

    // Same user: never flip loading→true (that remounts ProtectedRoute / kills fullscreen video).
    const sameUser = loadedUserId === user.id;
    if (showLoading && !sameUser) setLoading(true);
    try {
      const { data } = await supabase
        .from('organization_members')
        .select(`
          role,
          organizations (
            *,
            plans (*)
          )
        `)
        .eq('user_id', user.id)
        .single();

      if (data && data.organizations) {
        setOrg(data.organizations as unknown as Organization);
        setRole(data.role as 'admin' | 'fin_ops' | 'employee');
      }
    } catch (err) {
      console.error('Error fetching org:', err);
    } finally {
      setLoadedUserId(user.id);
      setLoading(false);
    }
  }, [user, loadedUserId]);

  // Only re-bootstrap when the signed-in user id changes — not on every token refresh.
  useEffect(() => {
    if (!user) {
      setOrg(null);
      setRole(null);
      setLoadedUserId(null);
      setLoading(false);
      return;
    }
    if (loadedUserId === user.id) return;
    void fetchOrg(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- user.id is the load key
  }, [user?.id]);

  const pending = loading || Boolean(user && loadedUserId !== user.id);

  return (
    <OrgContext.Provider value={{ org, role, loading: pending, refreshOrg: fetchOrg }}>
      {children}
    </OrgContext.Provider>
  );
};

export const useOrg = () => {
  const context = useContext(OrgContext);
  if (context === undefined) {
    throw new Error('useOrg must be used within an OrgProvider');
  }
  return context;
};
